<script setup lang="ts">
import { bannerService } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { BANNER_CONTAINER_ID } from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import { useGetActiveLicenseInformation } from "~/modules/license/composables/useGetActiveLicenseInformation";
import type { License } from "~/modules/license/models/License.interface";
import { getLicenseUploadErrors } from "~/modules/license/utils/getLicenseUploadErrors";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { isLoading, result, error, getActiveLicenseInformation } =
  useGetActiveLicenseInformation({
    organizationCode: currentOrganization.value?.code,
  });

const license: Ref<License | undefined> = ref();

/**
 * Get the license
 */
async function fetchLicense() {
  await getActiveLicenseInformation(true);
  if (error.value) {
    license.value = undefined;
  } else {
    license.value = result.value;
  }
}

/**
 * Display the error in the banner if the error is defined, otherwise hide the banner
 * @param error
 */
function displayError(error?: LxcError) {
  if (error) {
    bannerService.error({
      message: getLicenseUploadErrors(error),
      container: `#${BANNER_CONTAINER_ID}`,
      dangerouslyUseHTMLString: true,
    });
  } else {
    bannerService.hide();
  }
}

onMounted(fetchLicense);
</script>

<template>
  <page-component :is-loading="isLoading" :error="error">
    <license-content
      :organization-code="currentOrganization?.code"
      :license="license"
      @upload-success="fetchLicense"
      @upload-error="displayError"
    />
  </page-component>
</template>
